import {SupportedLocales} from '#types/locale.js';
import {pluralize} from '#utils/strings.js';

const evaluationWindowUnitSingular: Record<string, string> = {
  minutes: 'minute',
  hours: 'hour',
  days: 'day',
  weeks: 'week',
};

const evaluationWindowUnitSingularES: Record<string, string> = {
  minutes: 'minuto',
  hours: 'hora',
  days: 'día',
  weeks: 'semana',
};

export const journeysLanguage = {
  [SupportedLocales.EN]: {
    typeSelect: {
      label: 'Journey Type',
    },
    typeFilter: {
      label: 'Type',
      searchPlaceholder: 'Search journey types',
      emptyStateMessage: 'No journey types available to filter with.',
      ariaLabel: 'Filter journeys by type',
    },
    typeIndicator: {
      standard: 'Standard',
      realtime: 'Realtime',
      unknown: 'Unknown journey type',
    },
    uid2: {
      label: 'Use raw UID2',
      description: (destination: string, useCredentials = false) =>
        useCredentials
          ? `Map an email or phone field to resolve UID2 with your operator credentials when customers reach this Journey destination in ${destination}.`
          : `Send the dataset group’s configured raw UID2 identifiers to ${destination} when customers reach this Journey destination.`,
      learnMore: 'Learn more',
      unavailable:
        'UID2 is no longer available for this destination. Turn it off to configure other match fields.',
    },
    actions: {
      cancel: 'Cancel',
      apply: 'Confirm',
      actionsHeader: 'Actions',
      actionsMenu: 'Journey actions',
      copy: 'Copy',
      copySuccess: 'Journey copied',
      copyError: 'Failed to copy journey',
    },
    alertsPanel: {
      copyError: 'Copy error',
      errorCopied: 'Copied',
      errorCopyFailed: 'Copy failed',
    },
    savedNodeLibrary: {
      modalTitleEdit: 'Edit Node',
      modalTitleCreate: 'Save Node to Library',
      modalTitleView: 'Custom Node Library',
      titleLabel: 'Title',
      descriptionLabel: 'Description',
      loading: 'Loading...',
      emptyState: "You don't have any saved nodes yet.",
      searchPlaceholder: 'Search for a custom node',
      noResults: (query: string) => `No results found for "${query}".`,
      createdBy: (name: string) => `created by ${name}`,
      addButton: 'Add',
      savedNodeActions: 'Saved node actions',
      editButton: 'Edit',
      deleteButton: 'Delete',
      usedPrefix: 'used ',
      usedTime: 'time',
      usedTimes: 'times',
      cancelButton: 'Cancel',
      saveButton: 'Save',
      closeButton: 'Close',
      deleteConfirmTitle: 'Delete Custom Node',
      deleteConfirmDescription: (title: string) =>
        `Are you sure you want to delete "${title}"? This action cannot be undone.`,
      deleteConfirmButton: 'Delete',
      titleDuplicate: 'A node with this title already exists in the library.',
      notifications: {
        savedSuccess: 'Node saved to library',
        saveFailed: 'Node failed to save to library',
        updatedSuccess: 'Node updated',
        updateFailed: 'Node failed to update',
        deletedSuccess: 'Node deleted from library',
        deleteFailed: 'Node failed to delete from library',
        addFailed: 'Node failed to add to journey',
      },
    },
    hardResetModal: {
      title: 'Hard Reset the Journey',
      description:
        'This will reset all users to the entry node, and removes any records of previous executions (Note: Exports cannot be undone)',
    },
    simulateModal: {
      title: 'Simulate journey',
      description:
        'Run a full-audience warehouse dry run into an isolated table. Destination exports, trigger webhooks, and CRM actions are suppressed. Delay nodes are treated as elapsed; realtime wait-for behavior is not simulated.',
      dryRun: 'Dry run full audience',
      saveFirst: 'Save your changes before simulating.',
      cancel: 'Cancel',
      dryRunStarted:
        'Dry run started. You can leave this page and come back when it finishes.',
      dryRunError: 'Journey could not be dry-run',
      eventDescription:
        'Submit a sample event to a new or existing isolated dry run. External actions are suppressed and delays are treated as elapsed. Wait For nodes require a later matching event.',
      eventLabel: 'Sample event (JSON)',
      eventPlaceholder:
        'Paste an event containing the configured user identity fields',
      eventInvalid: 'Enter a valid JSON object.',
      runLabel: 'Dry run',
      newRun: 'New dry run',
      continueRun: (n: number) => `Continue dry run ${n}`,
      runEvent: 'Simulate event',
      starting: 'Starting…',
      runsError:
        'Could not load existing dry runs. Close this dialog and try again.',
      runUnavailable:
        'This dry run can no longer be continued. Select a new dry run.',
    },
    runsPanel: {
      title: 'Runs',
      eventCount: (n: number) => `${n} event${n === 1 ? '' : 's'} submitted`,
      waiting: 'Waiting for another event',
      notMatched: 'Event did not match',
      alreadyCompleted: 'This user has already completed the journey',
      alreadyProcessed: 'Event already processed',
      eventProcessed: 'Event processed',
      empty: 'No dry runs yet.',
      dryRun: (n: number) => `Dry run ${n}`,
      clear: 'Clear',
      running: 'Running',
      completed: 'Completed',
      failed: 'Failed',
    },
    nodeUsersPanel: {
      title: 'Activity',
      subtitle: 'Users who reached this step',
      menuItem: 'Activity',
      openFromMetrics: 'Show activity for this step',
      close: 'Close',
      tableLabel: 'Users who reached this step',
      user: 'User',
      state: 'State',
      lastUpdated: 'Last updated',
      states: {
        pending: 'Waiting here',
        completed: 'Passed through',
        exited: 'Exited',
      },
      total: (count: number) =>
        `${count.toLocaleString('en-US')} ${count === 1 ? 'visit' : 'visits'} to this step`,
      maskedNote:
        'User ids are masked because the dataset field that identifies users is flagged as PII.',
      filteredTotal: (count: number) =>
        `${count.toLocaleString('en-US')} ${count === 1 ? 'visit matches' : 'visits match'} your search or filter`,
      searchLabel: 'User ID',
      searchPlaceholder: 'User ID',
      searchUnavailableMasked:
        'Search is unavailable because user ids are masked for you.',
      stateFilterLabel: 'Filter by state',
      stateFilterPlaceholder: 'All states',
      stateGuide: {
        button: 'What do the states mean?',
        title: 'What the states mean',
        intro:
          "Each row is one visit to this step. A visit's state changes as the user moves on.",
        definitions: {
          pending:
            'At this step now and not yet moved on, for example waiting out a delay. In real-time journeys only steps that hold users, such as delays and waits, show this.',
          completed:
            'The step is finished with this user, who moved on to the next step or ended the journey here.',
          exited:
            'Marked as exited from this step. Users who leave the journey through an exit rule currently appear as Passed through.',
        },
      },
      noResults: 'No visits match your search or filter',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      rowsPerPage: 'Rows per page',
      pageOf: (page: number, pageCount: number) =>
        `Page ${page} of ${pageCount}`,
      loading: 'Loading users',
      empty: 'No users have reached this step yet.',
      notReady: 'No user data is available for this journey yet.',
      error: 'Users could not be loaded.',
    },
    dryRunBanner: {
      running: (table: string | null) =>
        table
          ? `Dry run in progress. Writing to ${table}. You can leave this page.`
          : 'Dry run in progress. You can leave this page.',
      inProgress: 'Dry run in progress.',
      failed: (message: string | null) =>
        message ? `Dry run failed. ${message}` : 'Dry run failed.',
    },
    dryRunReport: {
      title: 'Dry run results',
      empty: 'No dry run yet.',
      backToEditor: 'Back to editor',
      stateTable: (table: string) => `Warehouse table: ${table}`,
    },
    manualModeModal: {
      title: 'Manual Mode Run Confirmation',
      description: (manualRunType: string) =>
        `Are you sure you want to run ${manualRunType}?`,
      runUntilThisNode: 'until this node',
      runOnlyThisNode: 'only this node',
      runFromThisNode: 'from this node',
      actions: {
        cancel: 'Cancel',
        apply: 'Confirm',
      },
    },
    delayNode: {
      timeRangeOption: 'Within a time range',
      timeRangeDescription:
        'Select the days and time range when users can advance to the next step.',
      startTimeLabel: 'Start time',
      endTimeLabel: 'End time',
      invalidTime: 'Enter a valid time in 24-hour HH:MM format',
      invalidTimeRange: 'End time must be later than start time',
    },
    triggerNode: {
      configureTab: 'Configure',
      settingsTab: 'Settings',
      maxAttempts: 'Maximum attempts',
      retryableStatusCodes: 'Retryable status codes',
      retryableStatusCodesTooltip:
        'Comma-separated HTTP status codes to retry, for example: 429, 500, 503.',
      perUser: 'Call the API once per user',
      rateLimitEnabled: 'Limit API calls for this trigger node',
      rateLimitMaxCalls: 'Maximum API calls',
      rateLimitWindow: 'Time window',
      rateLimitUnit: 'Time window unit',
      rateLimitUnits: {minutes: 'Minutes', hours: 'Hours'},
      responseFields: 'Response Fields',
      addResponseField: 'Add Response Field',
      responseFieldSourceError:
        'Source must be status_code, body, payload.<field>, or headers.<field>',
      jsonResponseFieldSourceError:
        'Source must be a valid JMESPath expression',
      requestBodyTooltip:
        'You can use Handlebars to format the body. The template can use the "users" variable with all fields from the primary dataset. Any custom attribute defined in downstream nodes is available as "glca_<custom_attribute_label>".',
    },
    waitForNode: {
      name: 'Wait for',
      modalTitle: 'Wait for',
      modalDescription:
        'Select the realtime event a journey member must trigger before moving to the next step.',
      realtimeEventLabel: 'Realtime Event',
      selectPlaceholder: 'Select',
      selectRealtimeEvent: 'Select realtime event',
      noRealtimeEvents: 'No realtime events exist for this dataset group.',
      libraryDescription: 'Wait for realtime event',
      realtimeEventRequired: 'Realtime event is required',
      evaluationWindowEnabled: 'Set evaluation window',
      evaluationWindowValue: 'Window duration',
      evaluationWindowUnit: 'Time unit',
      evaluationWindowValueRequired: 'Enter a window duration greater than 0',
      evaluationWindowUnitRequired: 'Select a time unit',
      units: {
        minutes: 'Minutes',
        hours: 'Hours',
        days: 'Days',
        weeks: 'Weeks',
      },
      evaluationWindowSummary: (value: number, unit: string) =>
        `Evaluate for ${pluralize(value, evaluationWindowUnitSingular[unit] ?? unit)}`,
      noEvaluationWindowSummary: 'Evaluate until the event occurs',
      evaluationWindowMaxExceeded: 'Evaluation window cannot exceed 8 weeks',
      timeoutEdgeRequired: 'Connect a No path when an evaluation window is set',
      timeoutWindowRequired:
        'Set an evaluation window before connecting a No path',
      matchedEdgeRequired:
        'Connect exactly one Yes path when using evaluation-window exits',
    },
    customAttributes: {
      eventExpression: {
        expression: 'JMESPath expression',
        resultType: 'Result type',
        invalid: 'Enter a valid JMESPath expression',
        help: 'Evaluated against the incoming event. Missing fields return null. Choose String, Number, or Boolean for downstream criteria.',
        types: {
          string: 'String',
          number: 'Number',
          boolean: 'Boolean',
          json: 'JSON (object or array)',
        },
      },
      sectionHeading: 'Additional Custom Attributes',
      description:
        'Create extra attributes using static values, formulas, or related dataset fields. These will be included in the snapshot tables during export.',
      addButton: 'Add Custom Attribute',
      eventJoin: {
        source: 'Join source',
        warehouse: 'Warehouse relationship',
        event: 'Incoming event',
        eventField: 'Event field',
        tableField: 'Match table column',
        missingJoin: 'Choose an event field and a table column',
        datasetLabel: 'Dataset',
        datasetTooltip:
          "Choose a related dataset that's part of the audience's dataset group.",
      },
      fields: {
        labelAriaLabel: 'Attribute label',
        labelPlaceholder: 'Label',
        typeAriaLabel: 'Attribute type',
        valueAriaLabel: 'Attribute value',
        valuePlaceholder: 'Value',
        counterOperationAriaLabel: 'Counter operation',
        counterValueAriaLabel: 'Counter value',
        counterAmountPlaceholder: 'Amount',
        removeRowAriaLabel: 'Remove attribute row',
      },
      counterOperations: {
        set: 'Set',
        increment: 'Increment',
        decrement: 'Decrement',
      },
      types: {
        text: 'Text',
        'attribute-formula': 'Attribute Formula',
        'event-expression': 'Event Expression',
        'related-field': 'Related Dataset Fields',
        counter: 'Counter',
        'join-formula': 'Join Formula',
        'computed-field': 'User Traits',
      },
    },
    builder: {
      defaultGroupName: (letter: string) => `Group ${letter}`,
      criteriaCatchAllDefaultName: 'Everyone Else',
    },
    criteriaNode: {
      entryDataLoadError:
        'Unable to load journey data. Refresh the page and try again.',
    },
    variationNode: {
      attributionWindow: {
        label: 'Attribution window (days)',
        description:
          'Rewards within this many days after assignment are attributed to this variation.',
        summary: (days: string) =>
          `${days} ${days === '1' ? 'day' : 'days'} attribution`,
      },
    },
    decisioningNode: {
      contextFilter: {
        filters: 'Filter results',
        clearFilter: 'Clear filter',
        decisionPass: 'Decision pass',
        decisionPassDescription:
          'Each time a user enters this node counts as one pass. Filter results to one or more passes.',
        customFeature: (source: string) => `${source} (custom)`,
        updatingResults: 'Updating results…',
        noResults: 'No data matches the current filters.',
        search: 'Search values',
        missing: 'No value (missing)',
        emptyString: 'Empty string',
        min: 'Min',
        max: 'Max',
        minimum: 'Minimum (inclusive)',
        maximum: 'Maximum (inclusive)',
        invalidRange: 'Please enter a valid range.',
        noNumericBounds: 'No numeric bounds available.',
        loadError: 'Unable to load feature values.',
        loadingFilters: 'Loading filter options…',
      },
      contextFeatures: {
        requiredText: 'Select at least one feature',
      },
      assignmentCooldown: {
        valueRequired: 'Enter a cooldown duration',
        valueMin: 'Duration must be at least 1',
        unitRequired: 'Select a time unit',
      },
      initialDatasetPath: {
        invalidFormat: 'Use 2 or 3 identifier parts separated by dots.',
      },
    },
    experimentNode: {
      metric: {
        requiredText:
          'Significance experiments require a metric to be selected',
      },
    },
  },
  [SupportedLocales.ES]: {
    typeSelect: {
      label: 'Tipo de recorrido',
    },
    typeFilter: {
      label: 'Tipo',
      searchPlaceholder: 'Buscar tipos de recorrido',
      emptyStateMessage: 'No hay tipos de recorrido disponibles para filtrar.',
      ariaLabel: 'Filtrar recorridos por tipo',
    },
    typeIndicator: {
      standard: 'Estándar',
      realtime: 'Tiempo real',
      unknown: 'Tipo de recorrido desconocido',
    },
    uid2: {
      label: 'Usar UID2 sin procesar',
      description: (destination: string, useCredentials = false) =>
        useCredentials
          ? `Asigna un campo de correo electrónico o teléfono para resolver UID2 con tus credenciales del operador cuando los clientes lleguen a este destino del Journey en ${destination}.`
          : `Enviar los identificadores UID2 sin procesar configurados en el grupo de datos a ${destination} cuando los clientes lleguen a este destino del Journey.`,
      learnMore: 'Más información',
      unavailable:
        'UID2 ya no está disponible para este destino. Desactívalo para configurar otros campos de coincidencia.',
    },
    actions: {
      cancel: 'Cancelar',
      apply: 'Confirmar',
      actionsHeader: 'Acciones',
      actionsMenu: 'Acciones de la jornada',
      copy: 'Copiar',
      copySuccess: 'Jornada copiada',
      copyError: 'No se pudo copiar la jornada',
    },
    alertsPanel: {
      copyError: 'Copiar error',
      errorCopied: 'Copiado',
      errorCopyFailed: 'Error al copiar',
    },
    savedNodeLibrary: {
      modalTitleEdit: 'Editar nodo',
      modalTitleCreate: 'Guardar nodo en la biblioteca',
      modalTitleView: 'Biblioteca de nodos personalizados',
      titleLabel: 'Título',
      descriptionLabel: 'Descripción',
      loading: 'Cargando...',
      emptyState: 'Aún no tienes nodos guardados.',
      searchPlaceholder: 'Buscar un nodo personalizado',
      noResults: (query: string) =>
        `No se encontraron resultados para "${query}".`,
      createdBy: (name: string) => `creado por ${name}`,
      addButton: 'Añadir',
      savedNodeActions: 'Acciones del nodo guardado',
      editButton: 'Editar',
      deleteButton: 'Eliminar',
      usedPrefix: 'usado ',
      usedTime: 'vez',
      usedTimes: 'veces',
      cancelButton: 'Cancelar',
      saveButton: 'Guardar',
      closeButton: 'Cerrar',
      deleteConfirmTitle: 'Eliminar nodo personalizado',
      deleteConfirmDescription: (title: string) =>
        `¿Estás seguro de que quieres eliminar "${title}"? Esta acción no se puede deshacer.`,
      deleteConfirmButton: 'Eliminar',
      titleDuplicate: 'Ya existe un nodo con este título en la biblioteca.',
      notifications: {
        savedSuccess: 'Nodo guardado en la biblioteca',
        saveFailed: 'Error al guardar el nodo en la biblioteca',
        updatedSuccess: 'Nodo actualizado',
        updateFailed: 'Error al actualizar el nodo',
        deletedSuccess: 'Nodo eliminado de la biblioteca',
        deleteFailed: 'Error al eliminar el nodo de la biblioteca',
        addFailed: 'Error al añadir el nodo al recorrido',
      },
    },
    hardResetModal: {
      title: 'Reiniciar la Jornada',
      description:
        'Esto reiniciará a todos los usuarios al nodo de entrada y eliminará cualquier registro de ejecuciones anteriores (Nota: Las exportaciones no se pueden deshacer)',
    },
    simulateModal: {
      title: 'Simular recorrido',
      description:
        'Ejecuta un dry run de warehouse con toda la audiencia en una tabla aislada. Se suprimen las exportaciones de destination, los webhooks de trigger y las acciones de CRM. Los nodos de delay se consideran completados; no se simula el comportamiento realtime de wait-for.',
      dryRun: 'Dry run de toda la audiencia',
      saveFirst: 'Guarda los cambios antes de simular.',
      cancel: 'Cancelar',
      dryRunStarted:
        'Dry run iniciado. Puedes salir de esta página y volver cuando termine.',
      dryRunError: 'No se pudo ejecutar el dry run del recorrido',
      eventDescription:
        'Envía un evento de muestra a un dry run aislado nuevo o existente. Se suprimen las acciones externas y los delays se consideran completados. Los nodos Wait For requieren un evento posterior que coincida.',
      eventLabel: 'Evento de muestra (JSON)',
      eventPlaceholder:
        'Pega un evento con los campos de identidad de usuario configurados',
      eventInvalid: 'Introduce un objeto JSON válido.',
      runLabel: 'Dry run',
      newRun: 'Nuevo dry run',
      continueRun: (n: number) => `Continuar dry run ${n}`,
      runEvent: 'Simular evento',
      starting: 'Iniciando…',
      runsError:
        'No se pudieron cargar los dry runs existentes. Cierra este diálogo e inténtalo de nuevo.',
      runUnavailable:
        'Este dry run ya no puede continuar. Selecciona uno nuevo.',
    },
    runsPanel: {
      title: 'Ejecuciones',
      eventCount: (n: number) =>
        `${n} evento${n === 1 ? '' : 's'} enviado${n === 1 ? '' : 's'}`,
      waiting: 'Esperando otro evento',
      notMatched: 'El evento no coincidió',
      alreadyCompleted: 'Este usuario ya completó el recorrido',
      alreadyProcessed: 'Evento ya procesado',
      eventProcessed: 'Evento procesado',
      empty: 'Aún no hay dry runs.',
      dryRun: (n: number) => `Dry run ${n}`,
      clear: 'Quitar',
      running: 'En curso',
      completed: 'Completado',
      failed: 'Falló',
    },
    nodeUsersPanel: {
      title: 'Actividad',
      subtitle: 'Usuarios que llegaron a este paso',
      menuItem: 'Actividad',
      openFromMetrics: 'Mostrar la actividad de este paso',
      close: 'Cerrar',
      tableLabel: 'Usuarios que llegaron a este paso',
      user: 'Usuario',
      state: 'Estado',
      lastUpdated: 'Última actualización',
      states: {
        pending: 'Esperando aquí',
        completed: 'Pasó por aquí',
        exited: 'Salió',
      },
      total: (count: number) =>
        `${count.toLocaleString('es-ES')} ${count === 1 ? 'visita' : 'visitas'} a este paso`,
      maskedNote:
        'Los ids de usuario están ocultos porque el campo del dataset que identifica a los usuarios está marcado como PII.',
      filteredTotal: (count: number) =>
        `${count.toLocaleString('es-ES')} ${count === 1 ? 'visita coincide' : 'visitas coinciden'} con tu búsqueda o filtro`,
      searchLabel: 'ID de usuario',
      searchPlaceholder: 'ID de usuario',
      searchUnavailableMasked:
        'La búsqueda no está disponible porque los ids de usuario están ocultos para ti.',
      stateFilterLabel: 'Filtrar por estado',
      stateFilterPlaceholder: 'Todos los estados',
      stateGuide: {
        button: '¿Qué significan los estados?',
        title: 'Qué significan los estados',
        intro:
          'Cada fila es una visita a este paso. El estado de una visita cambia a medida que el usuario avanza.',
        definitions: {
          pending:
            'Está en este paso ahora y todavía no ha avanzado, por ejemplo esperando a que termine una espera. En los recorridos en tiempo real solo lo muestran los pasos que retienen usuarios, como las esperas.',
          completed:
            'El paso terminó con este usuario, que pasó al siguiente paso o terminó el recorrido aquí.',
          exited:
            'Marcado como salido de este paso. Los usuarios que salen del recorrido por una regla de salida aparecen ahora como Pasó por aquí.',
        },
      },
      noResults: 'Ninguna visita coincide con tu búsqueda o filtro',
      previousPage: 'Página anterior',
      nextPage: 'Página siguiente',
      rowsPerPage: 'Filas por página',
      pageOf: (page: number, pageCount: number) =>
        `Página ${page} de ${pageCount}`,
      loading: 'Cargando usuarios',
      empty: 'Ningún usuario ha llegado a este paso todavía.',
      notReady: 'Aún no hay datos de usuarios para este recorrido.',
      error: 'No se pudieron cargar los usuarios.',
    },
    dryRunBanner: {
      running: (table: string | null) =>
        table
          ? `Dry run en curso. Escribiendo en ${table}. Puedes salir de esta página.`
          : 'Dry run en curso. Puedes salir de esta página.',
      inProgress: 'Dry run en curso.',
      failed: (message: string | null) =>
        message ? `Dry run falló. ${message}` : 'Dry run falló.',
    },
    dryRunReport: {
      title: 'Resultados del dry run',
      empty: 'Aún no hay un dry run.',
      backToEditor: 'Volver al editor',
      stateTable: (table: string) => `Tabla del almacén: ${table}`,
    },
    manualModeModal: {
      title: 'Confirmación de Ejecución Manual',
      description: (manualRunType: string) =>
        `¿Estás seguro de que quieres ejecutar ${manualRunType}?`,
      runUntilThisNode: 'hasta este nodo',
      runOnlyThisNode: 'solo este nodo',
      runFromThisNode: 'desde este nodo',
      actions: {
        cancel: 'Cancelar',
        apply: 'Confirmar',
      },
    },
    delayNode: {
      timeRangeOption: 'Dentro de un intervalo de tiempo',
      timeRangeDescription:
        'Selecciona los días y el intervalo de tiempo en que los usuarios pueden avanzar al siguiente paso.',
      startTimeLabel: 'Hora de inicio',
      endTimeLabel: 'Hora de finalización',
      invalidTime: 'Introduce una hora válida en formato de 24 horas HH:MM',
      invalidTimeRange:
        'La hora de finalización debe ser posterior a la hora de inicio',
    },
    triggerNode: {
      configureTab: 'Configurar',
      settingsTab: 'Configuración',
      maxAttempts: 'Intentos máximos',
      retryableStatusCodes: 'Códigos de estado reintentables',
      retryableStatusCodesTooltip:
        'Códigos de estado HTTP separados por comas para reintentar, por ejemplo: 429, 500, 503.',
      perUser: 'Llamar a la API una vez por usuario',
      rateLimitEnabled: 'Limitar llamadas API para este nodo de activación',
      rateLimitMaxCalls: 'Máximo de llamadas API',
      rateLimitWindow: 'Ventana de tiempo',
      rateLimitUnit: 'Unidad de ventana de tiempo',
      rateLimitUnits: {minutes: 'Minutos', hours: 'Horas'},
      responseFields: 'Campos de respuesta',
      addResponseField: 'Agregar campo de respuesta',
      responseFieldSourceError:
        'La fuente debe ser status_code, body, payload.<field> o headers.<field>',
      jsonResponseFieldSourceError:
        'La fuente debe ser una expresión JMESPath válida',
      requestBodyTooltip:
        'Puedes usar Handlebars para formatear el body HTTP. La plantilla puede usar la variable "users" con todos los campos del conjunto de datos principal. Cualquier atributo personalizado definido en los nodos posteriores está disponible como "glca_<custom_attribute_label>".',
    },
    waitForNode: {
      name: 'Esperar',
      modalTitle: 'Esperar',
      modalDescription:
        'Seleccione el evento en tiempo real que un miembro del recorrido debe activar antes de pasar al siguiente paso.',
      realtimeEventLabel: 'Evento en tiempo real',
      selectPlaceholder: 'Seleccionar',
      selectRealtimeEvent: 'Seleccionar evento en tiempo real',
      noRealtimeEvents:
        'No existen eventos en tiempo real para este grupo de conjuntos de datos.',
      libraryDescription: 'Esperar evento en tiempo real',
      realtimeEventRequired: 'El evento en tiempo real es obligatorio',
      evaluationWindowEnabled: 'Establecer ventana de evaluación',
      evaluationWindowValue: 'Duración de la ventana',
      evaluationWindowUnit: 'Unidad de tiempo',
      evaluationWindowValueRequired:
        'Ingresa una duración de ventana mayor que 0',
      evaluationWindowUnitRequired: 'Selecciona una unidad de tiempo',
      units: {
        minutes: 'Minutos',
        hours: 'Horas',
        days: 'Días',
        weeks: 'Semanas',
      },
      evaluationWindowSummary: (value: number, unit: string) =>
        `Evaluar durante ${pluralize(value, evaluationWindowUnitSingularES[unit] ?? unit, {locale: 'es'})}`,
      noEvaluationWindowSummary: 'Evaluar hasta que ocurra el evento',
      evaluationWindowMaxExceeded:
        'La ventana de evaluación no puede superar las 8 semanas',
      timeoutEdgeRequired:
        'Conecta una ruta No cuando hay una ventana de evaluación configurada',
      timeoutWindowRequired:
        'Configura una ventana de evaluación antes de conectar una ruta No',
      matchedEdgeRequired:
        'Conecta exactamente una ruta Sí al usar salidas de ventana de evaluación',
    },
    customAttributes: {
      eventExpression: {
        expression: 'Expresión JMESPath',
        resultType: 'Tipo de resultado',
        invalid: 'Introduce una expresión JMESPath válida',
        help: 'Se evalúa sobre el evento entrante. Los campos ausentes devuelven null. Elige Cadena, Número o Booleano para los criterios posteriores.',
        types: {
          string: 'Cadena',
          number: 'Número',
          boolean: 'Booleano',
          json: 'JSON (objeto o matriz)',
        },
      },
      sectionHeading: 'Atributos Personalizados Adicionales',
      description:
        'Crea atributos adicionales usando valores estáticos, fórmulas o campos de conjuntos de datos relacionados. Para exportarlos, selecciónalos como campos de coincidencia o personalización en la configuración del destino.',
      addButton: 'Añadir Atributo Personalizado',
      eventJoin: {
        source: 'Origen de la unión',
        warehouse: 'Relación del almacén',
        event: 'Evento entrante',
        eventField: 'Campo del evento',
        tableField: 'Columna de coincidencia',
        missingJoin: 'Selecciona un campo del evento y una columna de la tabla',
        datasetLabel: 'Conjunto de datos',
        datasetTooltip:
          'Elige un conjunto de datos relacionado que forme parte del grupo de conjuntos de datos de la audiencia.',
      },
      fields: {
        labelAriaLabel: 'Etiqueta del atributo',
        labelPlaceholder: 'Etiqueta',
        typeAriaLabel: 'Tipo de atributo',
        valueAriaLabel: 'Valor del atributo',
        valuePlaceholder: 'Valor',
        counterOperationAriaLabel: 'Operación del contador',
        counterValueAriaLabel: 'Valor del contador',
        counterAmountPlaceholder: 'Cantidad',
        removeRowAriaLabel: 'Eliminar fila de atributo',
      },
      counterOperations: {
        set: 'Establecer',
        increment: 'Incrementar',
        decrement: 'Decrementar',
      },
      types: {
        text: 'Texto',
        'attribute-formula': 'Fórmula de atributo',
        'event-expression': 'Expresión de evento',
        'related-field': 'Campos de conjunto de datos relacionados',
        counter: 'Contador',
        'join-formula': 'Fórmula de unión',
        'computed-field': 'Rasgos de usuario',
      },
    },
    builder: {
      defaultGroupName: (letter: string) => `Grupo ${letter}`,
      criteriaCatchAllDefaultName: 'Todos los demás',
    },
    criteriaNode: {
      entryDataLoadError:
        'No se pudieron cargar los datos del recorrido. Actualiza la página e inténtalo de nuevo.',
    },
    variationNode: {
      attributionWindow: {
        label: 'Ventana de atribución (días)',
        description:
          'Las recompensas dentro de este número de días después de la asignación se atribuyen a esta variación.',
        summary: (days: string) =>
          `Atribución de ${days} ${days === '1' ? 'día' : 'días'}`,
      },
    },
    decisioningNode: {
      contextFilter: {
        filters: 'Filtrar resultados',
        clearFilter: 'Quitar filtro',
        decisionPass: 'Pase de decisión',
        decisionPassDescription:
          'Cada vez que un usuario entra en este nodo cuenta como un pase. Filtra los resultados a uno o más pases.',
        customFeature: (source: string) => `${source} (personalizada)`,
        updatingResults: 'Actualizando resultados…',
        noResults: 'Ningún dato coincide con los filtros actuales.',
        search: 'Buscar valores',
        missing: 'Sin valor (ausente)',
        emptyString: 'Cadena vacía',
        min: 'Mín',
        max: 'Máx',
        minimum: 'Mínimo (inclusivo)',
        maximum: 'Máximo (inclusivo)',
        invalidRange:
          'Introduce un rango válido: usa números finitos con el mínimo no mayor que el máximo.',
        noNumericBounds: 'No hay límites numéricos disponibles.',
        loadError: 'No se pudieron cargar los valores de la característica.',
        loadingFilters: 'Cargando opciones de filtro…',
      },
      contextFeatures: {
        requiredText: 'Seleccione al menos una característica',
      },
      assignmentCooldown: {
        valueRequired: 'Ingresa una duración de espera',
        valueMin: 'La duración debe ser al menos 1',
        unitRequired: 'Selecciona una unidad de tiempo',
      },
      initialDatasetPath: {
        invalidFormat:
          'Usa 2 o 3 partes de identificador separadas por puntos.',
      },
    },
    experimentNode: {
      metric: {
        requiredText:
          'Los experimentos de significancia requieren que se seleccione una métrica',
      },
    },
  },
};
